import CT from '@marketplace-ai/tags';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { Button } from '~/components/buttons';
import {
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
  MarketPanelHeader,
  MarketSegmentedControl,
} from '~/features/shared/market-content';
import { ContractCard } from './components/ContractCard';
import { ContractDetail } from './components/ContractDetail';
import { useContractsService } from './hooks/useContractsService';
import { FilterTab } from './types';

const TAGS = CT.employer.contracts.contractList;

const FILTER_TABS: { label: string; value: FilterTab; cyTag: string }[] = [
  { label: 'Active', value: 'active', cyTag: TAGS.filterTabActive },
  { label: 'Upcoming', value: 'upcoming', cyTag: TAGS.filterTabUpcoming },
  { label: 'Past', value: 'past', cyTag: TAGS.filterTabPast },
];

type RoleOption = 'all' | 'HIRING' | 'HIRED';

const ROLE_OPTIONS: { label: string; value: RoleOption; cyTag?: string }[] = [
  { label: 'All', value: 'all' },
  { label: 'Hiring', value: 'HIRING', cyTag: TAGS.roleToggleHiring },
  { label: 'Hired', value: 'HIRED', cyTag: TAGS.roleToggleHired },
];

export const MarketContractList = () => {
  const {
    contractsRef,
    employerUser,
    isProviderView,
    loading,
    contracts,
    selectedContract,
    detailError,
    openingLinkedContract,
    filterTab,
    roleFilter,
    search,
    hasMore,
    loadingMore,
    signError,
    signing,
    handleFilterTab,
    handleSearch,
    handleRoleToggle,
    handleSelect,
    handleLoadMore,
    handleSign,
    handleRequestTermination,
    handleWithdrawTermination,
  } = useContractsService();

  const hasContracts = contracts.length > 0;
  const hasActiveFilters =
    search.trim().length > 0 || roleFilter !== null || filterTab !== 'active';
  const emptyTitle = hasActiveFilters
    ? 'No contracts matching the selected filters'
    : 'No contracts yet';
  const emptyDescription = hasActiveFilters
    ? 'Try adjusting your search or filters.'
    : 'Contracts are created when a listing response is accepted.';

  if (!contractsRef || loading === undefined) {
    return <LoadingSpinner />;
  }

  // [MCV-1906] While more pages exist the loaded count is a lower bound.
  const countLabel = hasMore
    ? `${contracts.length}+ contracts`
    : `${contracts.length} contract${contracts.length === 1 ? '' : 's'}`;
  const roleValue: RoleOption = roleFilter ?? 'all';

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-cy="market-contracts">
      <div className="mb-8 grid grid-cols-1 items-start gap-3 min-[1024px]:grid-cols-12 min-[1360px]:mb-0 min-[1360px]:min-h-0 min-[1360px]:flex-1 min-[1360px]:items-stretch">
        <div className="col-span-12 h-[440px] min-[1024px]:col-span-5 min-[1024px]:max-[1359px]:h-[560px] min-[1360px]:col-span-4 min-[1360px]:h-full">
          <MarketContentPanel
            className="h-full"
            bodyClassName="flex min-h-0 flex-1 flex-col p-0"
            variant="scroll"
            header={
              <div className="flex flex-col gap-3">
                <MarketPanelHeader title="Contracts" subtitle={countLabel} />
                <MarketSegmentedControl
                  fullWidth
                  options={FILTER_TABS}
                  value={filterTab}
                  onChange={handleFilterTab}
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <input
                    type="text"
                    placeholder="Search contracts..."
                    aria-label="Search contracts"
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="min-w-0 flex-1 rounded-md border border-grey-900 px-3 py-2 text-sm outline-none focus:border-primary-50"
                    data-cy={TAGS.searchInput}
                  />
                  {/* The Hiring/Hired filter is an employer concept; the API ignores
                      `role` for providers, so showing it would be a dead control. */}
                  {!isProviderView && (
                    <MarketSegmentedControl
                      options={ROLE_OPTIONS}
                      value={roleValue}
                      onChange={(next) => handleRoleToggle(next === 'all' ? null : next)}
                    />
                  )}
                </div>
              </div>
            }
          >
            <div
              className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2"
              role="tabpanel"
              aria-label={`${filterTab} contracts`}
            >
              {loading ? (
                <MarketLoadingPanel label="Loading contracts" />
              ) : !hasContracts ? (
                <MarketEmptyPanel title={emptyTitle} description={emptyDescription} />
              ) : (
                <>
                  {contracts.map((contract) => (
                    <ContractCard
                      key={contract.id}
                      contract={contract}
                      isSelected={contract.id === selectedContract?.id}
                      showRoleBadge
                      isProviderView={isProviderView}
                      currentEmployerId={employerUser?.employerId}
                      onClick={() => handleSelect(contract.id)}
                    />
                  ))}
                  {hasMore && (
                    <Button
                      type="button"
                      size="sm"
                      colorVariant="outline"
                      className="mt-2 w-full shrink-0"
                      label={loadingMore ? 'Loading…' : 'Load more'}
                      disabled={loadingMore}
                      onClick={handleLoadMore}
                      cyTag={TAGS.loadMore}
                    />
                  )}
                </>
              )}
            </div>
          </MarketContentPanel>
        </div>
        <div className="col-span-12 h-[680px] min-[1024px]:col-span-7 min-[1024px]:max-[1359px]:h-[760px] min-[1360px]:col-span-8 min-[1360px]:h-full">
          <MarketContentPanel
            className="h-full"
            bodyClassName="min-h-0 flex-1 overflow-auto p-5 sm:p-6"
            variant="scroll"
          >
            {detailError ? (
              <p role="alert">{detailError}</p>
            ) : openingLinkedContract ? (
              <MarketLoadingPanel label="Opening contract" />
            ) : selectedContract ? (
              <ContractDetail
                contract={selectedContract}
                currentEmployerId={employerUser?.employerId}
                isProviderView={isProviderView}
                onSign={handleSign}
                signing={signing}
                signError={signError}
                onRequestTermination={handleRequestTermination}
                onWithdrawTermination={handleWithdrawTermination}
              />
            ) : (
              <MarketEmptyPanel
                title="Select a contract to view details"
                description="Pick a contract from the list to see its terms, parties, and history."
              />
            )}
          </MarketContentPanel>
        </div>
      </div>
    </div>
  );
};
